import { useState, useMemo, useEffect, useReducer } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { FiChevronUp, FiChevronDown, FiFilter, FiLoader } from "react-icons/fi";
import { CardBody, Collapse, Button, Spinner } from "reactstrap";
import { useQuery } from "react-query";
import moment from "moment";
import apiService from "services/api";
import NewEventModal from "./NewEventModal";
import ViewEventModal from "./ViewEventModal";
import { AddHomeworkModal } from "./AddHomeworkModal";
import {
  actions,
  transformData,
  initialModalState,
  initialDropdownState,
  initialFilterParams,
  eventColors,
  CustomToolbar,
} from "./constants";
import {
  filterReducer,
  newAttendanceModalReducer,
  initialNewAttendanceModalState,
  newAttendanceModalActions,
  filterActions,
} from "./reducers";
import { Dropdown } from "./CalendarDropdown";
import { FilterDropdown } from "./FilterDropdown";
import { AddStudentReportPage } from "./AddStudentReportPage";
import { useCallback } from "react";
import { firstDay, lastDay } from "utils/dateHelper";
import { useWindowDimensions } from "utils/useWindowDimensions";
import { setLocalStorage } from "utils/localStorage";
import { models, permission, permissionCheck } from "utils/permissionCheck";
import { fileResponseDownloader } from "utils/fileResponseDownloader";
import { Backdrop } from "components";

import { matchesLessonFilters, parseScheduleDate } from "./scheduleHelpers";

const localizer = momentLocalizer(moment);

const SchedulePage = () => {
  const { width: windowWidth } = useWindowDimensions();
  const [queryDateRange, setQueryDateRange] = useState({
    start: firstDay(),
    end: lastDay(),
  });
  const [currentSlotInfo, setCurrentSlotInfo] = useState({
    start: moment().format("HH:mm"),
    end: moment().format("HH:mm"),
    date: moment().format("YYYY-MM-DD"),
  });
  const [view, setView] = useState(windowWidth < 768 ? "day" : "month");
  const [currDate, setCurrDate] = useState(new Date());
  const [openViewModal, setOpenViewModal] = useState(initialModalState);
  const [openNewEventModal, setOpenNewEventModal] = useState(false);
  const [dropdownProps, setDropdownProps] = useState(initialDropdownState);
  const [collapseOpen, setCollapseOpen] = useState(false);

  const toggleCollapse = () => setCollapseOpen((prev) => !prev);

  const [filterParams, dispatchFilterParams] = useReducer(
    filterReducer,
    initialFilterParams,
  );
  const [newAttendanceModalProps, dispatchNewAttendanceModalProps] = useReducer(
    newAttendanceModalReducer,
    initialNewAttendanceModalState,
  );

  const [
    refetchingEventsDueToEditOrDelete,
    setRefetchingEventsDueToEditOrDelete,
  ] = useState(false);

  const isAbleToCreateNewEvent = permissionCheck(
    apiService.auth.token.get(),
    models.LESSON_RECORD,
    [permission.CREATE, permission.ADMIN_EDIT_PERMISSION],
    "or",
  );

  const isAbleToViewLeaves = permissionCheck(
    apiService.auth.token.get(),
    models.STAFF_APPLICATION,
    [permission.SCHEDULE_READ],
  );

  const isAbleToViewTeacherFilters = permissionCheck(
    apiService.auth.token.get(),
    models.ADMIN,
    [permission.SCHEDULE_READ],
  );

  const hasAdminRole = (() => {
    try {
      const roleName = apiService.auth.token.body()?.role?.name ?? "";
      return roleName.toLowerCase().includes("admin");
    } catch {
      return false;
    }
  })();

  const isAbleToDownloadDailyReport =
    hasAdminRole &&
    permissionCheck(apiService.auth.token.get(), models.REPORT, [
      permission.READ,
    ]);

  const [downloadingDailyReport, setDownloadingDailyReport] = useState(false);

  const downloadDailyReport = useCallback(() => {
    const today = moment().format("YYYY-MM-DD");
    setDownloadingDailyReport(true);
    apiService.report
      .dailyAttendanceReport({
        date_from: today,
        date_to: today,
      })
      .then((data) =>
        fileResponseDownloader({
          fileName: `${today}_daily_attendance_report.xlsx`,
          data,
        }),
      )
      .finally(() => setDownloadingDailyReport(false));
  }, []);
  const {
    data: events,
    isLoading: loadingLessonRecords,
    refetch,
    isRefetching: isFetchingLessonRecords,
  } = useQuery(
    ["lessonRecord", queryDateRange.start, queryDateRange.end],
    () =>
      apiService.lessonRecord
        .get(undefined, {
          date_from: queryDateRange.start,
          date_to: queryDateRange.end,
        })
        .then(transformData.lessonRecord),
    {
      refetchOnWindowFocus: (query) => query.isFetching,
      onSettled: () => setRefetchingEventsDueToEditOrDelete(false),
    },
  );

  const { data: leaves, isLoading: loadingLeaves } = useQuery(
    ["leave", queryDateRange.start, queryDateRange.end],
    () =>
      isAbleToViewLeaves
        ? apiService.leave
            .get(undefined, {
              date_from: queryDateRange.start,
              date_to: queryDateRange.end,
            })
            .then(transformData.leaves)
        : Promise.resolve([]),
    { refetchOnWindowFocus: true },
  );

  const { data: birthdays, isLoading: isLoadingBirthdays } = useQuery(
    ["birthdays"],
    () => apiService.student.birthdays().then(transformData.birthdays),
    { refetchOnWindowFocus: false, enabled: isAbleToViewTeacherFilters },
  );

  const { data: studentOptions, isLoading: loadingStudents } = useQuery(
    ["student", openNewEventModal, openViewModal.show],
    () =>
      isAbleToCreateNewEvent
        ? apiService.student
            .getAbsoluteMin(undefined, {
              show_deactivated: false,
            })
            .then((data) =>
              data.map((c) => ({
                value: c.id,
                label:
                  c.firstName +
                  " " +
                  c.lastName +
                  " (" +
                  c?.centre?.shortName +
                  ")",
                data: c,
              })),
            )
        : Promise.resolve([]),
    { enabled: openNewEventModal || openViewModal.show },
  );

  const { data: teacherOptions } = useQuery(
    "teacher",
    () =>
      isAbleToViewTeacherFilters
        ? apiService.staff.get().then((teachers) => [
            {
              value: "",
              label: "Unassigned",
            },
            ...teachers
              .map((teacher) => ({
                value: { ...teacher, toString: () => teacher.id },
                label: teacher.fullName,
              }))
              .sort((prev, curr) => prev.label.localeCompare(curr.label)),
          ])
        : Promise.resolve([]),
    {
      onSuccess: (teachers) => {
        dispatchFilterParams({
          type: filterActions.PREFILL_TEACHER_FILTER,
          payload: teachers,
        });
      },
      refetchOnWindowFocus: false,
    },
  );

  const hideOpenViewModal = () => setOpenViewModal(initialModalState);

  const showStudentReportModal = (lessonDetail) => {
    dispatchNewAttendanceModalProps({
      type: newAttendanceModalActions.OPEN,
      payload: lessonDetail,
    });
    hideOpenViewModal();
  };

  const filteredEventIndices = useMemo(
    () =>
      events
        ? events.reduce((filteredIndices, event, i) => {
            if (matchesLessonFilters(event, filterParams, isAbleToViewTeacherFilters))
              filteredIndices.push(i);

            return filteredIndices;
          }, [])
        : [],
    [filterParams, events, isAbleToViewTeacherFilters],
  );

  const filteredLeaveIndices = useMemo(
    () =>
      leaves
        ? leaves.reduce(
            (filteredIndices, leave, i) => {
              if (filterParams?.teacherFilter[leave.staff.id]?.show)
                filteredIndices.push(i);
              return filteredIndices;
            },
            // (filteredIndices = filterParams?.teacherFilter[
            // 	leave.staff.id
            // ]?.show
            // 	? [...filteredIndices, i]
            // 	: filteredIndices),
            [],
          )
        : [],
    [filterParams, leaves, view],
  );

  const handleSelectEvent = useCallback((event) => {
    setOpenViewModal({
      show: true,
      event,
      isLessonRecord: event.isLessonRecord,
    });
  }, []);

  const handleNavigate = useCallback(
    (date) => {
      if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return;
      let [start, end] = [firstDay(date, "month"), lastDay(date, "month")];
      setCurrDate(date);
      const newStart = moment(start).unix();
      const newEnd = moment(end).unix();
      const currStart = moment(queryDateRange.start).unix();
      const currEnd = moment(queryDateRange.end).unix();
      if (newStart >= currStart && newEnd <= currEnd) {
        return;
      }
      return setQueryDateRange({ start, end });
    },
    [queryDateRange],
  );

  const handleChangeView = useCallback((view) => {
    setView(view);
  }, []);

  const addNewEventAlert = useCallback(
    (slotInfo) => {
      const { start, end, box, bounds } = slotInfo;
      let [boundingRangeStart, boundingRangeEnd] = [
        firstDay(start, "month"),
        lastDay(end, "month"),
      ];
      const newStart = moment(boundingRangeStart).unix();
      const newEnd = moment(boundingRangeEnd).unix();
      const currStart = moment(queryDateRange.start).unix();
      const currEnd = moment(queryDateRange.end).unix();
      if (newStart < currStart || newEnd > currEnd) {
        setQueryDateRange({
          start: boundingRangeStart,
          end: boundingRangeEnd,
        });
      }
      const tempDate = moment(start);
      const [x, y] = bounds ? [bounds.x, bounds.y] : [box.clientX, box.clientY];
      setCurrDate(tempDate.toDate());
      start.setDate(start.getDate() + 1);
      setCurrentSlotInfo({
        start: start.toLocaleTimeString(),
        end: end.toLocaleTimeString(),
        date: tempDate.format("YYYY-MM-DD"),
      });

      if (!isAbleToCreateNewEvent) return triggerAction(actions.VIEW_DAY);

      if (view === "month") {
        setDropdownProps({ show: true, x, y });
      } else {
        setOpenNewEventModal(true);
      }
    },
    [view, queryDateRange],
  );

  const refetchEvents = () => {
    setRefetchingEventsDueToEditOrDelete(true);
    refetch();
  };

  const triggerAction = (action) => {
    switch (action) {
      case actions.VIEW_DAY:
        setView("day");
        break;
      case actions.NEW_EVENT:
        setOpenNewEventModal(true);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setLocalStorage("eventFilters", filterParams);
  }, [filterParams]);

  return (
    <div className="content">
      <NewEventModal
        open={openNewEventModal}
        hide={() => setOpenNewEventModal(false)}
        studentOptions={studentOptions}
        teacherOptions={teacherOptions}
        slotInfo={currentSlotInfo}
        refetchEvents={refetchEvents}
        isFetchingLessonRecords={isFetchingLessonRecords}
      />
      <ViewEventModal
        open={openViewModal.show}
        eventProp={openViewModal.event}
        isLessonRecord={openViewModal.isLessonRecord}
        hide={hideOpenViewModal}
        studentOptions={studentOptions}
        teacherOptions={teacherOptions}
        slotInfo={currentSlotInfo}
        showStudentReportModal={showStudentReportModal}
        refetch={refetch}
        refetchEvents={refetchEvents}
      />
      <AddStudentReportPage
        open={newAttendanceModalProps.show}
        lessonDetail={newAttendanceModalProps.lessonDetail}
        hide={() =>
          dispatchNewAttendanceModalProps({
            type: newAttendanceModalActions.HIDE,
          })
        }
        refetch={refetch}
      />
      {isAbleToCreateNewEvent && (
        <Dropdown
          {...dropdownProps}
          hide={() =>
            setDropdownProps((prev) => ({
              ...prev,
              show: false,
            }))
          }
          triggerAction={triggerAction}
        />
      )}
      <div className="position-relative">
        <CardBody className="py-1 px-2">
          <div className="d-flex justify-content-between mb-2">
            <div className="d-flex">
              {isAbleToCreateNewEvent && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => triggerAction(actions.NEW_EVENT)}
                >
                  <small className="font-weight-bold">New Event</small>
                </Button>
              )}
              {isAbleToDownloadDailyReport && (
                <Button
                  color="info"
                  size="sm"
                  className="ml-2"
                  disabled={downloadingDailyReport}
                  onClick={downloadDailyReport}
                >
                  <small className="font-weight-bold">
                    {downloadingDailyReport ? (
                      <Spinner size="sm" />
                    ) : (
                      "Daily Report"
                    )}
                  </small>
                </Button>
              )}
            </div>
            <div className="d-flex">
              <span
                className={`border btn d-flex align-items-center rounded border-info px-2 py-1 ${
                  collapseOpen ? "text-white bg-info" : "text-info"
                }`}
                id="filterCollapse"
                style={{ cursor: "pointer" }}
                onClick={toggleCollapse}
              >
                <FiFilter className="mr-1" />
                <span className="mr-1">Filters</span>
                {collapseOpen ? <FiChevronUp /> : <FiChevronDown />}
              </span>
            </div>
          </div>
          {view === "day" && (
            <div className="d-flex justify-content-end">
              <input
                type="date"
                className="form-control"
                style={{ width: 200 }}
                onChange={(event) =>
                  handleNavigate(parseScheduleDate(event.target.value))
                }
              />
            </div>
          )}
          <Collapse isOpen={collapseOpen}>
            <FilterDropdown
              filterParams={filterParams}
              dispatchFilterParams={dispatchFilterParams}
              teacherOptions={teacherOptions}
            />
          </Collapse>
          <Calendar
            selectable
            localizer={localizer}
            longPressThreshold={200}
            events={
              !loadingLessonRecords && !loadingLeaves
                ? [
                    ...(filterParams?.lessonRecordDisplay
                      ? filteredEventIndices.map((i) => events[i])
                      : []),
                    ...(filterParams?.staffApplicationDisplay
                      ? filteredLeaveIndices.map((i) => leaves[i])
                      : []),
                    ...(filterParams?.birthdaysDisplay
                      ? (birthdays ?? [])
                      : []),
                  ]
                : []
            }
            view={useMemo(() => view, [view])}
            defaultView="month"
            onNavigate={handleNavigate}
            defaultDate={new Date()}
            date={useMemo(() => currDate, [currDate])}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={addNewEventAlert}
            eventPropGetter={eventColors}
            onView={handleChangeView}
            dayLayoutAlgorithm="no-overlap"
            components={{ toolbar: CustomToolbar }}
            popup
            messages={{
              showMore: (total) => (
                <div
                  style={{ cursor: "pointer" }}
                  className="text-center"
                  onMouseOver={(e) => {
                    e.preventDefault();
                  }}
                >
                  {`+${total}`}
                </div>
              ),
            }}
          />

          <Backdrop
            open={
              loadingLessonRecords ||
              loadingLeaves ||
              refetchingEventsDueToEditOrDelete
            }
            fitToParent={true}
          >
            <div className="text-center position-absolute">
              <FiLoader size={40} className="animate-spin" />
            </div>
          </Backdrop>
        </CardBody>
      </div>
    </div>
  );
};

export default SchedulePage;
